import React, { useState, useEffect, useMemo, useRef } from "react";
import Papa from "papaparse";
import { ClipLoader } from "react-spinners";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";
import { 
  FaFileAudio, FaFileVideo, FaFilePdf, FaDownload, 
  FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaExpand,
  FaSearchPlus, FaSearchMinus, FaCompressArrowsAlt, FaUndo
} from "react-icons/fa";
import "./CSVGrid.css";

// --- Advanced Math Utils (Preserved) ---
const getMean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const getMedian = (arr) => {
  const mid = Math.floor(arr.length / 2);
  const nums = [...arr].sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};
const getStdDev = (arr, mean) => {
  return Math.sqrt(getMean(arr.map((x) => Math.pow(x - mean, 2))));
};
const getQuartiles = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q2 = getMedian(sorted);
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  return { q1, q2, q3 };
};
const getSkewness = (arr, mean, stdDev) => {
    const n = arr.length;
    if (n < 3 || stdDev === 0) return 0;
    const cubedDiffs = arr.map(x => Math.pow((x - mean) / stdDev, 3));
    return (n / ((n - 1) * (n - 2))) * cubedDiffs.reduce((a, b) => a + b, 0);
};
const getKurtosis = (arr, mean, stdDev) => {
    const n = arr.length;
    if (n < 4 || stdDev === 0) return 0;
    const fourthDiffs = arr.map(x => Math.pow((x - mean) / stdDev, 4));
    return ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) * fourthDiffs.reduce((a, b) => a + b, 0) - (3 * Math.pow(n - 1, 2)) / ((n - 2) * (n - 3));
};

// --- Sub-Components ---

const BoxPlot = ({ min, q1, median, q3, max }) => {
    if (min === undefined || max === undefined || isNaN(min) || isNaN(max)) {
        return <div className="viz-error">Insufficient Data</div>;
    }
    const range = max - min || 1;
    const getPos = (val) => ((val - min) / range) * 100;
    return (
        <div className="viz-boxplot-container">
            <svg width="100%" height="40">
                <line x1={`${getPos(min)}%`} y1="20" x2={`${getPos(max)}%`} y2="20" stroke="#52525b" strokeWidth="2" />
                <line x1={`${getPos(min)}%`} y1="10" x2={`${getPos(min)}%`} y2="30" stroke="#a1a1aa" strokeWidth="2" />
                <line x1={`${getPos(max)}%`} y1="10" x2={`${getPos(max)}%`} y2="30" stroke="#a1a1aa" strokeWidth="2" />
                <rect x={`${getPos(q1)}%`} y="10" width={`${getPos(q3) - getPos(q1)}%`} height="20" fill="rgba(102, 126, 234, 0.3)" stroke="#667eea" strokeWidth="2" />
                <line x1={`${getPos(median)}%`} y1="10" x2={`${getPos(median)}%`} y2="30" stroke="#f59e0b" strokeWidth="2" />
            </svg>
            <div className="boxplot-labels">
                <span>{min.toFixed(1)}</span>
                <span className="median-label">{(median || 0).toFixed(1)}</span>
                <span>{max.toFixed(1)}</span>
            </div>
        </div>
    );
};

// 1. Universal Header
const FileHeader = ({ fileName, type, url, meta = [] }) => (
  <div className="universal-header glass-panel">
    <div className="uh-left">
      <div className="uh-info">
        <h2>{fileName || "Untitled File"}</h2>
        <div className="uh-meta-row">
          <span className="uh-badge">{type.toUpperCase()}</span>
          {meta.map((m, i) => (
            <span key={i} className="uh-meta-item">{m}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// 2. Pro Image Viewer (With Zoom & Pan)
const ImageViewer = ({ url, fileName }) => {
  const [dims, setDims] = useState(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleZoom = (delta) => {
    setScale(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };

  const resetZoom = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };

  // Drag Logic
  const handleMouseDown = (e) => {
    if (scale > 1) {
        setIsDragging(true);
        setStartPos({ x: e.clientX - pos.x, y: e.clientY - pos.y });
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging) {
        setPos({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
    }
  };
  const handleMouseUp = () => setIsDragging(false);

  return (
    <div className="media-container">
      <FileHeader 
        fileName={fileName} 
        type="image" 
        url={url} 
        meta={dims ? [`${dims.w} × ${dims.h}px`] : []} 
      />
      <div className="stage-area centered">
        <div className="media-frame glass-panel">
            <div 
                className="media-wrapper image-wrapper" 
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <img 
                    src={url} 
                    alt={fileName}
                    className="image-content"
                    style={{ 
                        transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                        cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default"
                    }}
                    onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                />

                {/* Floating Controls Overlay (Visible on Hover) */}
                <div className="media-controls-overlay" style={{ alignItems: 'center', background: 'none', pointerEvents: 'none' }}>
                    <div className="image-controls-static" style={{ pointerEvents: 'auto' }}>
                        <div className="controls-row">
                            <button className="media-btn" onClick={() => handleZoom(0.2)}><FaSearchPlus /></button>
                            <span className="time-display">{Math.round(scale * 100)}%</span>
                            <button className="media-btn" onClick={() => handleZoom(-0.2)}><FaSearchMinus /></button>
                            <div style={{width: 1, height: 20, background: 'rgba(255,255,255,0.2)', margin: '0 10px'}}></div>
                            <button className="media-btn" onClick={resetZoom}><FaUndo /></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

// 3. Pro Audio Viewer (Studio Deck Style)
const AudioViewer = ({ url, fileName }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };
    const setMeta = () => setDuration(audio.duration);
    const onEnd = () => { setIsPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', setMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', setMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePlay = () => {
    if(isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const width = e.currentTarget.clientWidth;
    const clickX = e.nativeEvent.offsetX;
    const newTime = (clickX / width) * duration;
    audioRef.current.currentTime = newTime;
  };

  const formatTime = (s) => {
    if(!s) return "0:00";
    const min = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  return (
    <div className="media-container">
      <FileHeader fileName={fileName} type="audio" url={url} />
      <div className="stage-area centered">
        <div className="media-frame glass-panel">
            <div className="media-wrapper audio-mode">
                {/* Visual Placeholder Area */}
                <div className="audio-visual-stage">
                    <FaFileAudio className="audio-icon-pulse" />
                </div>

                {/* Deck Controls */}
                <div className="media-controls-overlay">
                    <div className="media-timeline" onClick={handleSeek}>
                        <div className="media-progress-fill" style={{width: `${progress}%`}}></div>
                    </div>

                    <div className="controls-row">
                        <div className="cr-left">
                            <button className="media-btn" onClick={togglePlay}>
                                {isPlaying ? <FaPause /> : <FaPlay />}
                            </button>
                            <span className="time-display">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                        </div>
                        <div className="cr-right">
                            <button className="media-btn" onClick={() => {audioRef.current.muted = !isMuted; setIsMuted(!isMuted)}}>
                                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                            </button>
                        </div>
                    </div>
                </div>
                <audio ref={audioRef} src={url} preload="metadata" />
            </div>
        </div>
      </div>
    </div>
  );
};

// 4. Pro Video Viewer (Cinema Style)
const VideoViewer = ({ url, fileName }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    const formatTime = (s) => {
        if (!s || isNaN(s)) return "0:00";
        const min = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${min}:${sec < 10 ? '0' : ''}${sec}`;
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const updateTime = () => {
            setCurrentTime(video.currentTime);
            setProgress((video.currentTime / video.duration) * 100);
        };
        const setMeta = () => setDuration(video.duration);
        const onEnd = () => { setIsPlaying(false); };
        video.addEventListener('timeupdate', updateTime);
        video.addEventListener('loadedmetadata', setMeta);
        video.addEventListener('ended', onEnd);
        return () => {
            video.removeEventListener('timeupdate', updateTime);
            video.removeEventListener('loadedmetadata', setMeta);
            video.removeEventListener('ended', onEnd);
        };
    }, []);

    const togglePlay = (e) => {
        if(e) e.stopPropagation();
        if (videoRef.current.paused) {
            videoRef.current.play();
            setIsPlaying(true);
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleSeek = (e) => {
        e.stopPropagation();
        const timeline = e.currentTarget;
        const clickX = e.nativeEvent.offsetX;
        const width = timeline.clientWidth;
        const newTime = (clickX / width) * duration;
        videoRef.current.currentTime = newTime;
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const toggleFullscreen = (e) => {
        e.stopPropagation();
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => console.log(err));
        } else {
            document.exitFullscreen();
        }
    };

    return (
        <div className="media-container">
            <FileHeader fileName={fileName} type="video" url={url} />
            <div className="stage-area centered">
                <div className="media-frame glass-panel">
                    <div 
                        className={`video-wrapper media-wrapper ${!isPlaying ? 'paused' : ''}`} 
                        ref={containerRef}
                        onClick={togglePlay}
                    >
                        <video ref={videoRef} src={url} controls={false} preload="metadata" />
                        
                        <div className="center-play-btn" onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
                            {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: '4px' }} />}
                        </div>
                        
                        <div className="media-controls-overlay" onClick={(e) => e.stopPropagation()}>
                            <div className="media-timeline" onClick={handleSeek}>
                                <div className="media-progress-fill" style={{ width: `${progress}%` }}></div>
                            </div>
                            <div className="controls-row">
                                <div className="cr-left">
                                    <button className="media-btn" onClick={togglePlay}>
                                        {isPlaying ? <FaPause /> : <FaPlay />}
                                    </button>
                                    <div className="vc-volume-group" style={{display: 'flex', alignItems: 'center'}}>
                                        <button className="media-btn" onClick={toggleMute}>
                                            {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                                        </button>
                                    </div>
                                    <span className="time-display">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </span>
                                </div>
                                <div className="cr-right">
                                    <button className="media-btn" onClick={toggleFullscreen}>
                                        <FaExpand />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 5. Pro Code/Text Viewer
const CodeViewer = ({ content, fileName, type, url }) => {
    const isJson = type === 'json';
    const displayContent = isJson ? JSON.stringify(content, null, 2) : content;

    return (
        <div className="media-container">
            <FileHeader fileName={fileName} type={isJson ? "code" : "text"} url={url} />
            <div className="code-editor-window glass-panel">
                <div className="editor-gutter">
                    {displayContent.split('\n').map((_, i) => (
                        <div key={i}>{i + 1}</div>
                    ))}
                </div>
                <div className="editor-content">
                    <pre>
                        <code>{displayContent}</code>
                    </pre>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---

export default function CSVGrid({ csvUrl, fileName }) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchTerm, setSearchTerm] = useState("");
  const [fileType, setFileType] = useState("unknown");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const rowsPerPage = 50;

  const getFileType = (url, name) => {
    if (!name && !url) return "unknown";
    const str = (name || url).toLowerCase();
    if (str.endsWith('.csv')) return "csv";
    if (str.endsWith('.json')) return "json";
    if (str.match(/\.(jpeg|jpg|png|gif|webp|svg)$/)) return "image";
    if (str.match(/\.(mp3|wav|ogg|m4a|flac)$/)) return "audio";
    if (str.match(/\.(mp4|webm|mov|mkv)$/)) return "video";
    if (str.endsWith('.pdf')) return "pdf";
    if (str.endsWith('.txt') || str.endsWith('.md')) return "text";
    return "generic";
  };

  useEffect(() => {
    if (!csvUrl) { setError("No File URL"); setLoading(false); return; }
    
    const type = getFileType(csvUrl, fileName);
    setFileType(type);
    setLoading(true);
    setError(null);

    if (["image", "audio", "video", "pdf", "generic"].includes(type)) {
        setLoading(false);
        return;
    }

    const proxyUrl = `https://calm-charm-production.up.railway.app/proxy?url=${encodeURIComponent(csvUrl)}`;

    fetch(proxyUrl)
      .then(res => res.ok ? res.text() : Promise.reject("Network Error"))
      .then(textData => {
        if (type === "csv") {
            Papa.parse(textData, {
                header: true, skipEmptyLines: true, dynamicTyping: true,
                complete: (res) => {
                  if (!res.data?.length) { setError("Empty CSV"); setLoading(false); return; }
                  setRows(res.data);
                  setColumns(Object.keys(res.data[0]));
                  performAdvancedAnalysis(res.data, Object.keys(res.data[0]));
                },
                error: (e) => { setError(e.message); setLoading(false); }
              });
        } else if (type === "json") {
            try {
                const json = JSON.parse(textData);
                setAnalysis({ rawJson: json });
                setLoading(false);
            } catch(e) { setError("Invalid JSON"); setLoading(false); }
        } else if (type === "text") {
            setAnalysis({ rawText: textData });
            setLoading(false);
        }
      })
      .catch(e => { setError(e.toString()); setLoading(false); });
  }, [csvUrl, fileName]);

  const performAdvancedAnalysis = (data, cols) => {
    const stats = {};
    const totalRows = data.length;

    cols.forEach(col => {
      const vals = data.map(r => r[col]);
      const validVals = vals.filter(v => v !== null && v !== "" && v !== undefined);
      const isNum = validVals.length > 0 && validVals.every(v => typeof v === 'number');
      
      let baseStats = {
        name: col,
        type: isNum ? "Numeric" : "Categorical",
        missing: totalRows - validVals.length,
        completeness: ((validVals.length / totalRows) * 100).toFixed(1),
        unique: new Set(validVals).size
      };

      if (isNum) {
        const mean = getMean(validVals);
        const stdDev = getStdDev(validVals, mean);
        const { q1, q2, q3 } = getQuartiles(validVals);
        const min = Math.min(...validVals);
        const max = Math.max(...validVals);
        const iqr = q3 - q1;
        const skew = getSkewness(validVals, mean, stdDev);
        const kurtosis = getKurtosis(validVals, mean, stdDev);
        const bucketCount = 15;
        const range = max - min || 1;
        const bucketSize = range / bucketCount;
        const histogram = Array.from({ length: bucketCount }, (_, i) => {
            const s = min + (i * bucketSize);
            const e = s + bucketSize;
            return { name: s.toFixed(1), value: validVals.filter(v => v >= s && v < e).length };
        });
        stats[col] = { ...baseStats, mean, stdDev, q1, median: q2, q3, min, max, iqr, skew, kurtosis, histogram };
      } else {
        const freq = {};
        validVals.forEach(v => freq[v] = (freq[v] || 0) + 1);
        const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 10);
        stats[col] = { ...baseStats, topValues: sorted.map(([k, v]) => ({ name: k, value: v })) };
      }
    });

    setAnalysis({ totalRows, totalCols: cols.length, columns: stats });
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;
    return [...rows].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig]);

  if (loading) return <div className="csvgrid-loading"><ClipLoader size={50} color="#667eea" /><p>Analysing Data...</p></div>;
  if (error) return <div className="csvgrid-error">Error: {error}</div>;

  // --- RENDERERS ---

  if (fileType === "image") return <ImageViewer url={csvUrl} fileName={fileName} />;
  if (fileType === "audio") return <AudioViewer url={csvUrl} fileName={fileName} />;
  if (fileType === "video") return <VideoViewer url={csvUrl} fileName={fileName} />;
  
  if (fileType === "json" && analysis?.rawJson) 
    return <CodeViewer content={analysis.rawJson} fileName={fileName} type="json" url={csvUrl} />;
    
  if (fileType === "text" && analysis?.rawText) 
    return <CodeViewer content={analysis.rawText} fileName={fileName} type="text" url={csvUrl} />;

  if (fileType === "pdf") {
      return (
        <div className="media-container">
            <FileHeader fileName={fileName} type="pdf" url={csvUrl} />
            <div className="stage-area centered">
              <div className="glass-panel pdf-card-pro">
                  <div className="pdf-icon-bg"><FaFilePdf /></div>
                  <h3>Portable Document Format</h3>
                  <p>This file cannot be previewed directly in the dashboard.</p>
                  <a href={csvUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                      Open PDF in New Tab
                  </a>
              </div>
            </div>
        </div>
      );
  }

  // CSV View (Untouched)
  if (fileType === "csv" && analysis?.columns) {
      const displayedRows = sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
      const filteredAnalysis = Object.values(analysis.columns).filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

      return (
        <div className="csvgrid-container">
          <div className="csvgrid-glass-header">
            <div className="header-content">
                <h2>Analytics Engine</h2>
                <div className="header-meta">
                    <div className="meta-tag"><span>ROWS</span><strong>{analysis.totalRows.toLocaleString()}</strong></div>
                    <div className="meta-tag"><span>COLS</span><strong>{analysis.totalCols}</strong></div>
                </div>
            </div>
            <div className="header-controls">
                <div className="search-bar">
                    <input 
                        type="text" 
                        placeholder="Search variables..." 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)}
                        disabled={activeTab !== 'dashboard'}
                    />
                </div>
                <div className="tab-pill">
                    <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Insights</button>
                    <button className={activeTab === 'raw' ? 'active' : ''} onClick={() => setActiveTab('raw')}>Data Grid</button>
                </div>
            </div>
          </div>

          {activeTab === 'dashboard' && (
            <div className="csvgrid-dashboard-grid">
                {filteredAnalysis.map((col) => (
                    <div key={col.name} className="glass-card">
                        <div className="card-header-main">
                            <div className="col-identity">
                                <h3>{col.name}</h3>
                                <span className={`badge ${col.type.toLowerCase()}`}>{col.type}</span>
                            </div>
                            <div className="completeness-indicator" title={`${col.completeness}% Complete`}>
                                <svg viewBox="0 0 36 36" className="circular-chart">
                                    <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="circle" strokeDasharray={`${col.completeness}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                </svg>
                            </div>
                        </div>

                        {col.type === 'Numeric' && (
                            <>
                                <div className="viz-primary">
                                    <ResponsiveContainer width="100%" height={100}>
                                        <AreaChart data={col.histogram}>
                                            <defs>
                                                <linearGradient id={`grad-${col.name}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.6}/>
                                                    <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <Tooltip contentStyle={{background: 'rgba(0,0,0,0.8)', border: '1px solid #333', borderRadius: '8px'}} itemStyle={{color: '#fff'}} labelStyle={{display: 'none'}} />
                                            <Area type="monotone" dataKey="value" stroke="#667eea" strokeWidth={2} fill={`url(#grad-${col.name})`} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="viz-secondary">
                                    <span className="viz-label">Distribution Spread (Box Plot)</span>
                                    <BoxPlot min={col.min} q1={col.q1} median={col.median} q3={col.q3} max={col.max} />
                                </div>
                                <div className="stats-matrix">
                                    <div className="stat-cell"><span>Mean</span><strong>{(col.mean ?? 0).toFixed(2)}</strong></div>
                                    <div className="stat-cell"><span>StdDev</span><strong>{(col.stdDev ?? 0).toFixed(2)}</strong></div>
                                    <div className="stat-cell"><span>Skew</span><strong>{(col.skew ?? 0).toFixed(2)}</strong></div>
                                    <div className="stat-cell"><span>Kurtosis</span><strong>{(col.kurtosis ?? 0).toFixed(2)}</strong></div>
                                </div>
                            </>
                        )}

                        {col.type === 'Categorical' && (
                            <>
                                <div className="viz-primary">
                                    <ResponsiveContainer width="100%" height={180}>
                                        <BarChart layout="vertical" data={col.topValues || []} margin={{left: 0}}>
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" width={80} tick={{fontSize: 10, fill: '#a1a1aa'}} />
                                            <Tooltip cursor={{fill: 'transparent'}} contentStyle={{background: '#000', border: '1px solid #333'}} />
                                            <Bar dataKey="value" fill="#c084fc" radius={[0, 4, 4, 0]} barSize={15} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="stats-matrix two-col">
                                    <div className="stat-cell"><span>Unique Values</span><strong>{col.unique}</strong></div>
                                    <div className="stat-cell"><span>Missing</span><strong>{col.missing}</strong></div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
          )}

          {activeTab === 'raw' && (
            <div className="glass-table-container">
                {/* START OF FIX: WRAPPER ADDED HERE */}
                <div className="table-scroll-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th className="sticky-idx">#</th>
                                {columns.map((col, i) => (
                                    <th key={i} onClick={() => handleSort(col)} className="sortable-th">
                                        {col} {sortConfig.key === col ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayedRows.map((row, rIdx) => (
                                <tr key={rIdx}>
                                    <td className="sticky-idx">{(currentPage - 1) * rowsPerPage + rIdx + 1}</td>
                                    {columns.map((col, cIdx) => (
                                        <td key={cIdx}>{row[col] !== null ? row[col] : <span className="null">NULL</span>}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* END OF FIX */}
                
                <div className="pagination-footer">
                      <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
                      <span>Page {currentPage} of {Math.ceil(analysis.totalRows / rowsPerPage)}</span>
                      <button disabled={currentPage >= Math.ceil(analysis.totalRows / rowsPerPage)} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
                </div>
            </div>
          )}
        </div>
      );
  }

  // Fallback
  return (
      <div className="media-container">
          <FileHeader fileName={fileName} type="generic" url={csvUrl} />
          <div className="stage-area centered">
             <div className="glass-panel pdf-card-pro">
                 
                 <h3>Preview Unavailable</h3>
                 <a href={csvUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">Download File</a>
             </div>
          </div>
      </div>
  );
}